/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Sunday, 17th February 2019
 *
 *  Portal - Login View
 */
import React from 'react';
import { observer } from 'mobx-react';
import { Switch, Route, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import LoginForm from 'components/Forms/LoginForm';
import PasswordReset from 'containers/pages/Login/sections/PasswordReset';
import RegistrationWizard from 'containers/pages/Login/sections/RegistrationWizard';
import CatchError from 'components/ErrorBoundaries/CatchError';
import { useStores } from 'stores/RootStore';

// Material UI
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';

// Components
import SignUp from 'containers/pages/Login/sections/SignUp';
import Footer from 'components/Footers/Footer';

const Login = props => {
  const { classes, theme } = props;
  const stores = useStores();

  // Update Page Title
  stores.appStore.setPage('Login');
  stores.portalStore.loading.hidePageLoader();

  if (stores.authStore.isAuth) {
    return <Redirect to='/' />;
  }

  // Dynamic Logo based on theme type
  const logo = () =>
    theme.palette.type === 'dark'
      ? require('assets/images/login-vb-shield-text-small-light.png')
      : require('assets/images/login-vb-shield-text-small.png');

  stores.appStore.debug('Container::Page::Login - Rendered successfully.');
  return (
    <div className={classes.root}>
      <Grid container className={classes.gridWrapper} direction='column' justify='center' alignItems='center'>
        <Grid container className={classes.gridCardContainer}>
          <Grid item xl={6} lg={7} md={10} sm={12} xs={11}>
            <Card>
              <CardContent className={classes.loginCard}>
                <Switch>
                  <Route path='/login/sign-up' render={props => <RegistrationWizard {...props} />} />
                  <Route
                    path='/login'
                    render={props => (
                      <Grid container justify='space-between' alignitems='center' direction='row'>
                        <Grid item className={classes.logoGrid} md={5} sm={5} xs={12}>
                          <Grid item className={classes.formGridItem} xs={12}>
                            <img alt='VendorBadge' src={logo()} />
                          </Grid>
                        </Grid>
                        <Grid item md={7} sm={7} xs={12} className={classes.formGrid}>
                          <Grid item xs={11} className={classes.formGridItem}>
                            <CatchError>
                              <Switch>
                                <Route exact path='/login' component={LoginForm} />
                                <Route exact path='/login/choose' component={SignUp} />
                                <Route exact path='/login/reset-password' component={PasswordReset} />
                                <Redirect from='*' to='/login' />
                              </Switch>
                            </CatchError>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                  />
                  <Redirect from='*' to='/login' />
                </Switch>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Grid container direction='row' alignItems='flex-end'>
          <Footer />
        </Grid>
      </Grid>
    </div>
  );
};

Login.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
};

const styles = theme => ({
  '@global html': {
    backgroundColor: theme.palette.background.default + '!important',
  },
  root: {
    display: 'flex',
  },
  gridWrapper: {
    minHeight: 'calc(100vh - 4px)',
    minWidth: '320px',
    backgroundColor: theme.palette.background.default,
  },
  gridCardContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loginCard: {
    paddingTop: '15px !important',
    paddingBottom: '15px !important',
    minWidth: '295px',
    [theme.breakpoints.up('sm')]: {
      paddingBottom: '20px !important',
      paddingTop: '20px !important',
      minWidth: '295px',
    },
  },
  formGrid: {
    paddingTop: '20px',
    borderLeft: '0',
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    [theme.breakpoints.up('sm')]: {
      borderTop: '0',
      paddingTop: '0',
      marginTop: '0',
      borderLeft: '2px solid #e0e0e0',
      display: 'flex',
      justifyContent: 'center',
      textAlign: 'center',
    },
  },
  formGridItem: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '30px',
    marginBottom: '15px',
  },
  logoGrid: {
    paddingRight: '0',
    margin: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    minWidth: '225px',
    [theme.breakpoints.up('sm')]: {
      paddingRight: '16px',
      margin: 'auto',
      display: 'flex',
      justifyContent: 'center',
      textAlign: 'center',
      minWidth: '225px',
      alignItems: 'center',
    },
  },
  grow: {
    flexGrow: 1,
    width: '100%',
  },
});

export default withStyles(styles, { withTheme: true })(observer(Login));
